/**
 * When a game's props become visible in the tool.
 *
 * NFL runs on a weekly cycle and its projections aren't refreshed often enough early in the week to bet
 * off, so each game's props open at a fixed time the day before kickoff (noon Eastern). Games before
 * that point still appear in the schedule strip, muted, with their open time, and tapping one shows an
 * explainer instead of an empty table. Leagues without a rule are always open.
 *
 * Copy lives here too so the wording can be tuned in one place.
 */

export interface PropsOpenRule {
  /** Calendar days before the game's Eastern kickoff date. */
  daysBefore: number;
  /** Wall-clock time in Eastern on that day. */
  hourEastern: number;
  minuteEastern: number;
  /** Human wording of the rule, used in the explainer. */
  ruleText: string;
}

const EASTERN = 'America/New_York';

const OPEN_RULES: Record<string, PropsOpenRule> = {
  NFL: { daysBefore: 1, hourEastern: 12, minuteEastern: 0, ruleText: 'noon Eastern the day before kickoff' },
};

export const PROPS_OPEN_COPY = {
  /** Explainer title when no game is open yet. */
  allLockedTitle: (league: string) => `${league.toUpperCase()} props open ${getPropsOpenRule(league)?.ruleText ?? 'closer to kickoff'}`,
  /** Title on a locked game's page. `openTime` is already formatted for the user's clock. */
  lockedGameTitle: (matchup: string, openTime: string) => `Props for ${matchup} open ${openTime}`,
  body: 'We price props against the latest lines and injury news, so each game’s props open once its projections are current.',
  nextUp: (matchup: string, kickoff: string, openTime: string) => `Next up: ${matchup}, ${kickoff}. Props open ${openTime}.`,
  dfsPrompt: 'Looking for weekly projections? DFS projections are in the Data Hub all week.',
  dfsLinkText: 'Open the Data Hub',
  chipPrefix: 'Opens',
};

/** Dev/QA escape hatch: show every game regardless of the rule. */
export function isPropsOpenWindowIgnored(): boolean {
  return process.env.REACT_APP_PROPS_IGNORE_OPEN_WINDOW === 'true';
}

export function getPropsOpenRule(league: string): PropsOpenRule | null {
  return OPEN_RULES[league?.toUpperCase?.() ?? ''] ?? null;
}

/** Instant at which the game's props open, or null when the league has no rule or the kickoff is unparseable. */
export function getPropsOpenTime(startTime: string, league: string): Date | null {
  const rule = getPropsOpenRule(league);
  if (!rule) return null;
  const kickoff = new Date(startTime);
  if (isNaN(kickoff.getTime())) return null;

  const { year, month, day } = getZonedDateParts(kickoff, EASTERN);
  // Date.UTC handles day underflow (e.g. the 1st minus one day) for us
  return zonedWallTimeToUtc(year, month, day - rule.daysBefore, rule.hourEastern, rule.minuteEastern, EASTERN);
}

export function isGameLocked(startTime: string, league: string, now: number = Date.now()): boolean {
  if (isPropsOpenWindowIgnored()) return false;
  const openTime = getPropsOpenTime(startTime, league);
  return openTime !== null && now < openTime.getTime();
}

/** "Sat 12:00 PM" in the user's local time, matching the kickoff chips. */
export function formatPropsOpenTime(openTime: Date, options: { weekday?: boolean } = { weekday: true }): string {
  return openTime.toLocaleString('en-US', {
    ...(options.weekday ? { weekday: 'short' } : {}),
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/** Chip text for a locked game: "Opens Sat 12:00 PM". */
export function lockedChipText(openTime: Date): string {
  return `${PROPS_OPEN_COPY.chipPrefix} ${formatPropsOpenTime(openTime)}`;
}

// ---- time zone helpers (no date library in this project) ----

function getZonedDateParts(date: Date, timeZone: string): { year: number; month: number; day: number; hour: number; minute: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  }).formatToParts(date);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  // Intl may report midnight as 24 with hour12: false in some engines
  const hour = get('hour') % 24;
  return { year: get('year'), month: get('month') - 1, day: get('day'), hour, minute: get('minute') };
}

/** Milliseconds the zone is ahead of UTC at the given instant (negative for the Americas). */
function getZoneOffsetMs(date: Date, timeZone: string): number {
  const p = getZonedDateParts(date, timeZone);
  const asIfUtc = Date.UTC(p.year, p.month, p.day, p.hour, p.minute, date.getUTCSeconds());
  return asIfUtc - Math.floor(date.getTime() / 60000) * 60000 - date.getUTCSeconds() * 1000;
}

/** Convert a wall-clock time in `timeZone` to the corresponding UTC instant (DST-aware). */
function zonedWallTimeToUtc(year: number, month: number, day: number, hour: number, minute: number, timeZone: string): Date {
  const guess = Date.UTC(year, month, day, hour, minute);
  const offset1 = getZoneOffsetMs(new Date(guess), timeZone);
  let result = guess - offset1;
  // One correction pass covers instants that land on the other side of a DST switch
  const offset2 = getZoneOffsetMs(new Date(result), timeZone);
  if (offset2 !== offset1) result = guess - offset2;
  return new Date(result);
}
